import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  RequestDetail,
  RequestStatus,
  RequestStatusRequest,
  RequestSummary,
  RequestsSummary as RequestsSummaryData,
} from '@blesk/contracts';
import { apiGet, apiPost, type QueryParams } from '../../shared/api';
import { usePagedList } from '../../shared/lists';
import { useProjectOptions, timezoneOf } from '../../shared/options';
import {
  CHANNEL_LABEL,
  REQUEST_ACTION_LABEL,
  REQUEST_STATUS_LABEL,
  REQUEST_TYPE_LABEL,
} from '../../shared/labels';
import { formatInProjectTz } from '../../shared/format';
import { useHasPermission, useSession } from '../../shared/session';
import { useUrlFilter } from '../../shared/filters';
import { PermissionGate } from '../shell/PermissionGate';
import { IntegrationBlock } from '../shell/IntegrationBlock';
import {
  B24TaskLink,
  Dialog,
  EmptyRow,
  ErrorNote,
  ExportCsvLink,
  Pager,
  PeriodFilter,
  StatToggle,
  orDash,
} from '../shell/ui';
import { plural } from '../shell/charts';

/** §10, §13 ТЗ. Раздел «Обращения». */
export function RequestsPage() {
  return (
    <PermissionGate permission="results.read">
      <RequestsInner />
    </PermissionGate>
  );
}

/** Переход, который сотрудник может запросить: в «Новое» не возвращаются (§5.1). */
type RequestAction = RequestStatusRequest['status'];

/** §5.1: Новое → Принято → В работе → Закрыто, из работы — Отклонено. Возврата в «Новое» нет. */
const NEXT_STATUSES: Record<RequestStatus, RequestAction[]> = {
  NEW: ['ACCEPTED'],
  ACCEPTED: ['IN_PROGRESS'],
  IN_PROGRESS: ['CLOSED', 'REJECTED'],
  CLOSED: [],
  REJECTED: [],
};

function RequestsInner() {
  const session = useSession();
  const [projectId, setProjectId] = useUrlFilter('projectId');
  const [type, setType] = useUrlFilter('type');
  const [status, setStatus] = useUrlFilter('status');
  const [number, setNumber] = useUrlFilter('number');
  const [from, setFrom] = useUrlFilter('from');
  const [to, setTo] = useUrlFilter('to');
  // §11.2: ссылка из задачи Bitrix24 ведёт на `/requests/{id}`
  const { id: idFromUrl } = useParams({ strict: false }) as { id?: string };
  const [cardId, setCardId] = useState<string | null>(idFromUrl ?? null);

  const projects = useProjectOptions();
  // один объект отбора на таблицу и на выгрузку (§14, E9): разойтись им негде
  const filters = { projectId, type, status, number, from, to };
  const list = usePagedList<RequestSummary>('requests', '/api/requests', filters);

  const zoneOf = (request: RequestSummary) =>
    timezoneOf(projects.data, request.projectId, session.defaultTimezone);

  // §4 КП: в автономном режиме задач в Bitrix24 не бывает вовсе, и колонка
  // из одних прочерков только отодвигает вправо всё остальное.
  const integrated = session.mode !== 'STANDALONE';
  const columns = integrated ? 8 : 7;

  return (
    <>
      <h1>Обращения</h1>
      <p className="muted">Три типа обращений; закрытие карточки не меняет параметры услуги.</p>

      <div className="toolbar">
        <label>
          Объект
          <select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
            <option value="">Все</option>
            {(projects.data ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Тип
          <select value={type} onChange={(event) => setType(event.target.value)}>
            <option value="">Все</option>
            {Object.entries(REQUEST_TYPE_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Статус
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">Все</option>
            {Object.entries(REQUEST_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Номер
          <input
            type="text"
            value={number}
            placeholder="например, CR-2026-000001"
            onChange={(event) => setNumber(event.target.value)}
          />
        </label>
        <PeriodFilter from={from} to={to} onFrom={setFrom} onTo={setTo} />
        <span className="toolbar__spacer" />
        <ExportCsvLink path="/api/requests/export" filters={filters} />
      </div>

      <ErrorNote error={list.error} />

      <RequestsSummary filters={filters} status={status} onStatus={setStatus} />

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              {/* Номер и время создания — одна пара: таблица отсортирована по
                  времени, и разносить их по колонкам значит платить 145 px за
                  факт, который читают вместе с номером. Объект и администратор
                  сведены по той же причине (замер 18.08.2026). */}
              <th>Обращение</th>
              <th>Тип</th>
              <th>Объект и администратор</th>
              <th>Канал</th>
              <th>Статус</th>
              <th>Менеджер</th>
              {integrated && <th>Задача B24</th>}
              <th />
            </tr>
          </thead>
          <tbody>
            {list.items.length === 0 && <EmptyRow colSpan={columns} loading={list.isPending} />}
            {list.items.map((request) => (
              <tr key={request.id}>
                <td>
                  {request.number}
                  <div className="cell-note">
                    {formatInProjectTz(request.createdAt, zoneOf(request))}
                  </div>
                </td>
                <td>{REQUEST_TYPE_LABEL[request.type]}</td>
                <td>
                  {request.projectName}
                  <div className="cell-note">{request.personName}</div>
                </td>
                <td>
                  {CHANNEL_LABEL[request.channel as keyof typeof CHANNEL_LABEL] ?? request.channel}
                </td>
                <td>{REQUEST_STATUS_LABEL[request.status]}</td>
                <td>{orDash(request.managerSnapshotName)}</td>
                {integrated && (
                  <td>
                    <B24TaskLink taskId={request.bitrixTaskId} />
                  </td>
                )}
                <td className="row-actions">
                  <button type="button" onClick={() => setCardId(request.id)}>
                    Открыть
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pager page={list.page} perPage={list.perPage} total={list.total} onPage={list.setPage} />

      {cardId && <RequestCard id={cardId} onClose={() => setCardId(null)} />}
    </>
  );
}

/**
 * §13, §14 ТЗ. Сводка над таблицей раздела.
 *
 * Считает сервер по тому же отбору, что и таблицу (`/api/requests/summary`):
 * сумма видимых строк — это сумма одной страницы, а не отбора. Фильтры сюда
 * приходят тем же объектом, что уходит в список и в выгрузку.
 *
 * Статусы сервер считает без фильтра статуса, поэтому плитка остаётся
 * переключателем: из «Закрыто» видно, сколько лежит в «Новое», и туда есть
 * чем вернуться.
 */
function RequestsSummary({
  filters,
  status,
  onStatus,
}: {
  filters: QueryParams;
  status: string;
  onStatus: (status: string) => void;
}) {
  const summary = useQuery({
    queryKey: ['requests', 'summary', filters],
    queryFn: () => apiGet<RequestsSummaryData>('/api/requests/summary', filters),
    placeholderData: keepPreviousData,
  });

  const data = summary.data;
  if (summary.error) return <ErrorNote error={summary.error} />;
  if (!data) return <p className="muted">Считаем сводку…</p>;

  return (
    <section className="section-block summary" aria-label="Сводка по отбору">
      <h2>
        Сводка по отбору{' '}
        <span className="muted">
          {data.total === 0
            ? 'обращений нет'
            : `${plural(data.total, ['обращение', 'обращения', 'обращений'])} · открытых ${data.open}`}
        </span>
      </h2>

      <h3 className="stat-subhead">По статусам</h3>
      <div className="stat-grid">
        <StatToggle
          value={data.total}
          label="Все"
          active={status === ''}
          onClick={() => onStatus('')}
        />
        {data.byStatus.map((row) => (
          <StatToggle
            key={row.status}
            value={row.count}
            label={REQUEST_STATUS_LABEL[row.status]}
            active={status === row.status}
            onClick={() => onStatus(status === row.status ? '' : row.status)}
          />
        ))}
      </div>

      <h3 className="stat-subhead">По типам</h3>
      <div className="stat-grid">
        {/* Крупно — то, с чем ещё работать: закрытые обращения внимания
            не требуют, но общее число нужно, чтобы понять долю. */}
        {data.byType.map((row) => (
          <div className="stat" key={row.type}>
            <div className="stat__value">{row.open}</div>
            <div className="stat__label">
              {REQUEST_TYPE_LABEL[row.type]}: открыто из {row.total}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function RequestCard({ id, onClose }: { id: string; onClose: () => void }) {
  const canChangeStatus = useHasPermission('results.status');
  const queryClient = useQueryClient();

  const request = useQuery({
    queryKey: ['requests', 'detail', id],
    queryFn: () => apiGet<RequestDetail>(`/api/requests/${id}`),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['requests'] });
  };

  const changeStatus = useMutation({
    mutationFn: (next: RequestAction) => apiPost(`/api/requests/${id}/status`, { status: next }),
    onSuccess: refresh,
  });

  const data = request.data;

  return (
    <Dialog
      title={data ? `Обращение ${data.number} · ${REQUEST_TYPE_LABEL[data.type]}` : 'Обращение'}
      onClose={onClose}
    >
      <ErrorNote error={request.error ?? changeStatus.error} />
      {!data && request.isPending && <p className="muted">Загрузка…</p>}
      {data && (
        <>
          <dl className="detail">
            <dt>Объект</dt>
            <dd>{data.projectName}</dd>
            <dt>Администратор</dt>
            <dd>{data.personName}</dd>
            <dt>Канал</dt>
            <dd>{CHANNEL_LABEL[data.channel as keyof typeof CHANNEL_LABEL] ?? data.channel}</dd>
            <dt>Статус</dt>
            <dd>{REQUEST_STATUS_LABEL[data.status]}</dd>
            <dt>Менеджер на момент события</dt>
            <dd>{orDash(data.managerSnapshotName)}</dd>
            <dt>Задача Bitrix24</dt>
            <dd>
              <B24TaskLink taskId={data.bitrixTaskId} />
            </dd>
            <dt>Создано</dt>
            <dd>{formatInProjectTz(data.createdAt, data.projectTimezone)}</dd>
            <dt>Закрыто</dt>
            <dd>{formatInProjectTz(data.closedAt, data.projectTimezone)}</dd>
          </dl>

          <div className="section-block">
            <h3>Текст обращения</h3>
            <pre className="snapshot">{data.text}</pre>
            {data.textTruncated && <p className="muted">Текст обрезан по ограничению длины.</p>}
          </div>

          {/* Служебный идентификатор прячется так же, как в карточке инцидента:
              в реквизитах он читался как поле, которое человек должен понимать */}
          <details className="tech">
            <summary>Технические подробности</summary>
            <dl className="kv">
              <dt>Внешний ключ</dt>
              <dd>
                <code>{data.externalKey}</code>
              </dd>
            </dl>
          </details>

          {canChangeStatus && NEXT_STATUSES[data.status].length > 0 && (
            <div className="row-actions">
              {NEXT_STATUSES[data.status].map((next) => (
                <button
                  key={next}
                  type="button"
                  className={next === 'REJECTED' ? 'btn-danger' : 'btn-primary'}
                  disabled={changeStatus.isPending}
                  onClick={() => changeStatus.mutate(next)}
                >
                  {REQUEST_ACTION_LABEL[next]}
                </button>
              ))}
            </div>
          )}

          <IntegrationBlock
            integration={data.integration}
            timezone={data.projectTimezone}
            onRetried={refresh}
          />
        </>
      )}
    </Dialog>
  );
}
